import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs } from "@design-system/Tabs/Tabs";
import { TransactionRuleManagement } from "@components/transactionRules/TransactionRuleManagementContext/TransactionRuleManagement";
import { useSearchParams } from "react-router-dom";
import { JsonParam, StringParam, useQueryParams } from "use-query-params";
import { FiltersTypes } from "@common/types/filters/general";
import { RuleGroupType } from "@common/types/transactionRule";
import { useCategoryLabelGetter } from "@hooks/useCategoryLabelGetter";
import { useEntities } from "@hooks/useEntities";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useRulesPageFiltersCache } from "@hooks/view/useRulesPageFiltersCache";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useProductTipBanner } from "@hooks/useProductTipBanner";
import {
    useWorkspaceRuleGroups,
    useDefaultAccountingRules,
    useRuleExecutionJobSubscription,
} from "@api/rules.api";
import {
    useWorkspaceAccounts,
    useWorkspaceAccrualAccounts,
} from "@api/account.api";
import { useClasses } from "@api/class.api";
import {
    defaultRuleSources,
    defaultRuleStatuses,
    FiltersChangeFn,
} from "../../pageHeader/filters/lib";
import { RulesPageHeader } from "./RulesPageHeader";
import styles from "./RulesPage.module.scss";
import {
    buildRulesItems,
    isCategoryFilterApplicableTab,
    isCounterpartyOrClassFilterApplicableTab,
} from "./utils";
import { RuleReviewFilters } from "./ruleReviewFilters";
import { RuleValueFilters } from "./ruleValueFilters";
import {
    RULES_FILTERS_SEARCH_PARAM,
    RULES_RULE_ID_SEARCH_PARAM,
    RULES_TAB_SEARCH_PARAM,
    rulesUrlFiltersSchema,
} from "./lib/rulesUrl";
import { RulesTable } from "./RulesTable/RulesTable";
import { RulesPageNav } from "./RulesPageNav";
import { TransactionEnrichmentPausedBanner } from "@components/accountingMigration/TransactionEnrichmentPausedBanner";
import { RulesProductTipBanner } from "./RulesProductTipBanner";

const RULE_GROUP_TYPES: RuleGroupType[] = Object.values(RuleGroupType);
const isRuleGroupType = (value: string | null): value is RuleGroupType =>
    RULE_GROUP_TYPES.some((type) => type === value);

export const RulesPageAccessed = () => {
    useRuleExecutionJobSubscription();

    const { showProductTipBanner, dismissProductTipBanner } =
        useProductTipBanner("initialProductTipRules");

    const categoryLabelGetter = useCategoryLabelGetter();
    const entities = useEntities();
    const financialAccounts = useFinancialAccounts();
    const { classesMap, isActivated: isClassesEnabled } = useClasses();
    const isAccrualLedgerEnabled = useIsAccrualLedgerEnabled();
    const accounts = useWorkspaceAccounts();
    const accrualAccounts = useWorkspaceAccrualAccounts();
    const allAccounts = useMemo(
        () => [...(accounts.data ?? []), ...(accrualAccounts.data ?? [])],
        [accounts.data, accrualAccounts.data],
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const routeRuleId = searchParams.get(RULES_RULE_ID_SEARCH_PARAM);
    const { enabled: isGlFirst } = useIsGlFirst();
    const { categoryGlMapping } = useTypedFlags();

    const tabParam = searchParams.get(RULES_TAB_SEARCH_PARAM);
    const activeTab = isRuleGroupType(tabParam)
        ? tabParam
        : RuleGroupType.TRANSACTION;

    const [queryParams, setQueryParams] = useQueryParams({
        [RULES_FILTERS_SEARCH_PARAM]: JsonParam,
        [RULES_TAB_SEARCH_PARAM]: StringParam,
    });
    const urlFilters = queryParams[RULES_FILTERS_SEARCH_PARAM];
    const seedFilters = useMemo(() => {
        if (!urlFilters) {
            return undefined;
        }
        const { data, success: isSuccess } =
            rulesUrlFiltersSchema.safeParse(urlFilters);
        return isSuccess ? data : undefined;
    }, [urlFilters]);
    const seedCategoryId = seedFilters?.categoryId;
    const seedLedgerAccountIds = seedFilters?.ledgerAccountIds;
    const seedEntityIds = seedFilters?.entityIds;
    const isCategoryTab = isCategoryFilterApplicableTab(activeTab);

    const setActiveTab = useCallback(
        (tab: RuleGroupType) => {
            setSearchParams(
                (currentSearchParams) => {
                    const nextSearchParams = new URLSearchParams(
                        currentSearchParams,
                    );
                    nextSearchParams.set(RULES_TAB_SEARCH_PARAM, tab);
                    return nextSearchParams;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const ruleGroups = useWorkspaceRuleGroups();

    const [cachedFilters, setCachedFilters] = useRulesPageFiltersCache();
    const [filters, setFilters] = useState<
        Pick<
            FiltersTypes,
            | "entityIds"
            | "ruleStatuses"
            | "category"
            | "ruleSources"
            | "ruleActionTypes"
        > &
            RuleValueFilters &
            RuleReviewFilters
    >({
        entityIds: entities.map((entity) => entity.id),
        ruleStatuses: defaultRuleStatuses,
        ruleSources: defaultRuleSources,
        ...cachedFilters,
    });

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (payload) =>
            setFilters((current) => ({
                ...current,
                ...payload,
                ledgerAccounts: undefined,
            })),
        [],
    );

    useEffect(() => {
        setCachedFilters({
            entityIds: filters.entityIds,
            ruleStatuses: filters.ruleStatuses,
            ruleSources: filters.ruleSources,
            ruleActionTypes: filters.ruleActionTypes,
            counterpartiesIds: filters.counterpartiesIds,
            classes: filters.classes,
            coaAccounts: filters.coaAccounts,
            accrualCoaAccounts: filters.accrualCoaAccounts,
            ruleReviewStatuses: filters.ruleReviewStatuses,
            ruleReviewerIds: filters.ruleReviewerIds,
            ruleReviewerMode: filters.ruleReviewerMode,
        });
    }, [
        filters.entityIds,
        filters.ruleActionTypes,
        filters.ruleSources,
        filters.ruleStatuses,
        filters.counterpartiesIds,
        filters.classes,
        filters.coaAccounts,
        filters.accrualCoaAccounts,
        filters.ruleReviewStatuses,
        filters.ruleReviewerIds,
        filters.ruleReviewerMode,
        setCachedFilters,
    ]);

    useEffect(() => {
        if (!seedLedgerAccountIds?.length) {
            return;
        }
        setFilters((current) => ({
            ...current,
            entityIds: seedEntityIds ?? [],
            category: undefined,
            ruleActionTypes: undefined,
            counterpartiesIds: undefined,
            classes: undefined,
            coaAccounts: undefined,
            accrualCoaAccounts: undefined,
            ruleReviewStatuses: undefined,
            ruleReviewerIds: undefined,
            ruleReviewerMode: undefined,
            ledgerAccounts: seedLedgerAccountIds,
        }));
        setQueryParams(
            { [RULES_FILTERS_SEARCH_PARAM]: undefined },
            "replaceIn",
        );
    }, [seedLedgerAccountIds, seedEntityIds, setQueryParams]);

    useEffect(() => {
        if (!categoryGlMapping || !seedCategoryId) {
            return;
        }
        setFilters((current) => ({ ...current, category: [seedCategoryId] }));
        setQueryParams(
            isRuleGroupType(tabParam)
                ? { [RULES_FILTERS_SEARCH_PARAM]: undefined }
                : {
                      [RULES_FILTERS_SEARCH_PARAM]: undefined,
                      [RULES_TAB_SEARCH_PARAM]: RuleGroupType.ACCOUNTING,
                  },
            "replaceIn",
        );
    }, [categoryGlMapping, seedCategoryId, tabParam, setQueryParams]);

    const defaultAccountingRules = useDefaultAccountingRules();

    const [searchQuery, setSearchQuery] = useState("");

    const effectiveFilters = useMemo(() => {
        const isCounterpartyOrClassTab =
            isCounterpartyOrClassFilterApplicableTab(activeTab);
        const scopedFilters = {
            ...filters,
            ruleActionTypes:
                activeTab === RuleGroupType.TRANSACTION
                    ? filters.ruleActionTypes
                    : undefined,
            counterpartiesIds: isCounterpartyOrClassTab
                ? filters.counterpartiesIds
                : undefined,
            classes:
                isCounterpartyOrClassTab && isClassesEnabled
                    ? filters.classes
                    : undefined,
            accrualCoaAccounts: isAccrualLedgerEnabled
                ? filters.accrualCoaAccounts
                : undefined,
        };

        return categoryGlMapping
            ? {
                  ...scopedFilters,
                  category: isCategoryTab ? filters.category : undefined,
              }
            : scopedFilters;
    }, [
        categoryGlMapping,
        filters,
        isCategoryTab,
        activeTab,
        isClassesEnabled,
        isAccrualLedgerEnabled,
    ]);

    const filteredRuleGroups = useMemo(
        () =>
            buildRulesItems({
                search: searchQuery,
                categoryLabelGetter,
                ruleGroups: ruleGroups.data?.ruleGroups,
                entities,
                financialAccounts,
                counterparties: ruleGroups.data?.counterparties ?? [],
                normalizedDescriptions:
                    ruleGroups.data?.normalizedDescriptions ?? [],
                ruleReviews: ruleGroups.data?.ruleReviews ?? {},
                filters: effectiveFilters,
                accounts: allAccounts,
                classesMap,
                activeTab,
            }),
        [
            searchQuery,
            categoryLabelGetter,
            ruleGroups.data,
            entities,
            financialAccounts,
            effectiveFilters,
            allAccounts,
            classesMap,
            activeTab,
        ],
    );

    const accountingRuleGroups = useMemo(
        () =>
            buildRulesItems({
                search: searchQuery,
                categoryLabelGetter,
                ruleGroups: defaultAccountingRules.data?.ruleGroup
                    ? [defaultAccountingRules.data?.ruleGroup]
                    : [],
                entities,
                financialAccounts,
                counterparties: ruleGroups.data?.counterparties ?? [],
                normalizedDescriptions:
                    ruleGroups.data?.normalizedDescriptions ?? [],
                ruleReviews: ruleGroups.data?.ruleReviews ?? {},
                filters: effectiveFilters,
                accounts: allAccounts,
                classesMap,
                activeTab,
            }),
        [
            searchQuery,
            categoryLabelGetter,
            defaultAccountingRules.data?.ruleGroup,
            entities,
            financialAccounts,
            ruleGroups.data?.counterparties,
            ruleGroups.data?.normalizedDescriptions,
            ruleGroups.data?.ruleReviews,
            effectiveFilters,
            allAccounts,
            classesMap,
            activeTab,
        ],
    );

    const routeRuleGroup = useMemo(
        () =>
            routeRuleId
                ? ruleGroups.data?.ruleGroups.find((group) =>
                      group.rules?.some((rule) => rule.id === routeRuleId),
                  )
                : undefined,
        [ruleGroups.data?.ruleGroups, routeRuleId],
    );

    useEffect(() => {
        if (routeRuleGroup) {
            setActiveTab(routeRuleGroup.type);
        }
    }, [routeRuleGroup, setActiveTab]);

    const closeRouteRuleDrawer = useCallback(() => {
        setSearchParams(
            (currentSearchParams) => {
                const nextSearchParams = new URLSearchParams(
                    currentSearchParams,
                );
                nextSearchParams.delete(RULES_RULE_ID_SEARCH_PARAM);
                return nextSearchParams;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return (
        <div className={classNames(styles.rules, "settings__content")}>
            <TransactionEnrichmentPausedBanner />
            {showProductTipBanner ? (
                <RulesProductTipBanner onDismiss={dismissProductTipBanner} />
            ) : null}
            <TransactionRuleManagement>
                <Tabs.Root
                    value={activeTab}
                    onValueChange={(v) => isRuleGroupType(v) && setActiveTab(v)}
                >
                    <RulesPageHeader
                        setSearch={setSearchQuery}
                        search={searchQuery}
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        activeTab={activeTab}
                        navigation={<RulesPageNav />}
                    />

                    <RulesTable
                        activeTab={activeTab}
                        isLoading={ruleGroups.isPending}
                        groups={[...(filteredRuleGroups ?? [])]}
                        accountingGroup={
                            activeTab === RuleGroupType.ACCOUNTING && !isGlFirst
                                ? accountingRuleGroups?.[0]
                                : undefined
                        }
                        counterparties={ruleGroups.data?.counterparties ?? []}
                        normalizedDescriptions={
                            ruleGroups.data?.normalizedDescriptions ?? []
                        }
                        transactionCounts={
                            ruleGroups.data?.transactionCounts ?? {}
                        }
                        matchingCounts={ruleGroups.data?.matchingCounts ?? {}}
                        ruleSources={
                            activeTab === RuleGroupType.TRANSACTION
                                ? filters.ruleSources
                                : undefined
                        }
                        defaultAccountingTransactionCounts={
                            defaultAccountingRules.data?.transactionCounts ?? {}
                        }
                        routeRuleId={routeRuleId}
                        onRouteRuleClose={closeRouteRuleDrawer}
                    />
                </Tabs.Root>
            </TransactionRuleManagement>
        </div>
    );
};
